<template>
  <section>
    <ul class="list-none overflow-hidden">
      <li v-for="facetItem in visibleFacetItems" :key="facetItem.key">
        <RevCheckbox
          :id="facetItem.id"
          v-model="facetItemCheckedValues"
          class="mb-8"
          :name
          :value="facetItem.value"
        >
          <template #label>
            <span class="line-clamp-1">
              {{
                i18n(translations.nameAndCount, {
                  name: facetItem.name,
                  count: facetItem.count,
                })
              }}
            </span>
          </template>
        </RevCheckbox>
      </li>
    </ul>
    <RevLink
      v-if="isShowAllToggleVisible"
      class="mt-8"
      underlined
      @click="handleToggleShowAll"
    >
      {{ visibilityText }}
    </RevLink>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { FacetType } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-products.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevLink } from '@ds/components/Link'

import { MAX_VISIBLE_FACET_COUNT, MODEL_EVENT } from './Facet.constants'
import translations from './Facet.translations'

const props = withDefaults(
  defineProps<{
    name: string
    facet: FacetType
    value?: string[] | string
  }>(),
  {
    value: () => [],
  },
)

const emit = defineEmits([MODEL_EVENT])

const i18n = useI18n()

const showAll = ref(false)
const facetItemCheckedValues = ref(
  Array.isArray(props.value) ? props.value : [props.value],
)

const facetItems = computed(() =>
  Object.keys(props.facet).map((key) => ({
    name: key,
    id: `${props.name}_${key}`,
    value: key.toString(),
    key,
    count: props.facet[key],
  })),
)

const maxVisibleFacetCount = MAX_VISIBLE_FACET_COUNT

const isShowAllToggleVisible = computed(
  () => facetItems.value.length > maxVisibleFacetCount,
)

const visibilityText = computed(() =>
  showAll.value ? i18n(translations.showLess) : i18n(translations.showMore),
)

const visibleFacetItems = computed(() => {
  const numberOfFacetItems = showAll.value
    ? facetItems.value.length
    : maxVisibleFacetCount

  return facetItems.value.slice(0, numberOfFacetItems)
})

watch(facetItemCheckedValues, () => {
  emit(MODEL_EVENT, props.name, facetItemCheckedValues.value)
})

watch(
  () => props.value,
  (newValue) => {
    facetItemCheckedValues.value = Array.isArray(newValue)
      ? newValue
      : [newValue]
  },
)

function handleToggleShowAll() {
  showAll.value = !showAll.value
}
</script>
